import { useEffect, useRef, useState } from 'react';
import { Settings } from 'lucide-react';
import * as ToggleGroup from '@radix-ui/react-toggle-group';
import { useSettings, type Settings as SettingsShape } from '@/settings/settings';

/** Compact header control surfacing voice, sound, and motion preferences. */
export function SettingsMenu() {
  const { settings, setVoice, setSound, setMotion } = useSettings();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-haspopup="dialog"
        aria-label="Experience settings"
        className="inline-flex items-center gap-1 text-xs text-muted transition-colors hover:text-accent"
      >
        <Settings size={14} aria-hidden />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Experience settings"
          className="absolute right-0 top-full z-50 mt-1.5 w-56 rounded-lg border border-border bg-surface p-3 shadow-lg"
        >
          <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-wide text-muted">
            Experience
          </p>

          <div className="space-y-1.5">
            <Row label="Voice">
              <Switch
                checked={settings.voice}
                onChange={setVoice}
                label="Voice (Web Speech)"
              />
            </Row>
            <Row label="Sound cues">
              <Switch checked={settings.sound} onChange={setSound} label="Sound cues" />
            </Row>
          </div>

          <div className="mt-3">
            <p className="mb-1.5 text-[10px] text-muted">Motion</p>
            <ToggleGroup.Root
              type="single"
              value={settings.motion}
              onValueChange={(v) => v && setMotion(v as SettingsShape['motion'])}
              aria-label="Motion preference"
              className="inline-flex w-full overflow-hidden rounded-md border border-border bg-surface-2 text-[10px]"
            >
              {(['system', 'reduce', 'full'] as const).map((m) => (
                <ToggleGroup.Item
                  key={m}
                  value={m}
                  className="flex-1 py-1 capitalize text-muted transition-colors data-[state=on]:bg-accent/15 data-[state=on]:text-accent"
                >
                  {m}
                </ToggleGroup.Item>
              ))}
            </ToggleGroup.Root>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-xs">
      <span>{label}</span>
      {children}
    </div>
  );
}

function Switch({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
}) {
  return (
    <button
      role="switch"
      aria-checked={checked}
      aria-label={label}
      onClick={() => onChange(!checked)}
      className={`relative h-4 w-7 rounded-full transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent ${
        checked ? 'bg-accent' : 'bg-border'
      }`}
    >
      <span
        className={`absolute top-0.5 h-3 w-3 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-3.5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}
